import { z } from 'zod';

/** Tenant branding and custom domains (ADR-0036). */

export const CUSTOM_DOMAIN_STATUSES = ['none', 'pending', 'active', 'failed'] as const;

export const brandingSchema = z.object({
  display_name: z.string().nullable(),
  logo_url: z.string().nullable(),
  receipt_header: z.string().nullable(),
  receipt_footer: z.string().nullable(),
  custom_domain: z.string().nullable(),
  custom_domain_status: z.enum(CUSTOM_DOMAIN_STATUSES),
  /** DNS record the customer must create for a pending domain. */
  custom_domain_target: z.string().nullable(),
  version: z.number().int(),
});
export type Branding = z.infer<typeof brandingSchema>;

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable();

export const updateBrandingRequestSchema = z.object({
  display_name: optionalText(80),
  /** HTTPS image URL (e.g. an uploaded media URL). */
  logo_url: z
    .url({ protocol: /^https$/ })
    .max(500)
    .nullable()
    .or(z.literal('').transform(() => null)),
  receipt_header: optionalText(300),
  receipt_footer: optionalText(300),
  /** `version` from GET (0 before the first save). */
  expected_version: z.number().int().min(0),
});
export type UpdateBrandingRequest = z.input<typeof updateBrandingRequestSchema>;

/** A hostname the tenant owns, e.g. `menu.restoranku.id` (no scheme, no path, no wildcard). */
export const hostnameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(253)
  .regex(
    /^(?=.{4,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/,
    'Enter a domain such as menu.restaurant.id',
  );

export const setCustomDomainRequestSchema = z.object({ hostname: hostnameSchema });

/** Public branding shown on the guest menu. */
export const guestBrandSchema = z.object({
  display_name: z.string().nullable(),
  logo_url: z.string().nullable(),
});
export type GuestBrand = z.infer<typeof guestBrandSchema>;
